import { PermissionGate } from "@components/PermissionGate";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ActionIcon, Button, ComboboxItem, Menu, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconEye, IconPencil, IconX } from "@tabler/icons-react";
import { Row } from "@tanstack/react-table";

import { openDeleteModal } from "@components/DeleteModal";
import { LastUpdatedTimestamp } from "@components/LastUpdated";

import {
  showLoadingNotification,
  showSuccessNotification,
} from "@utils/showNotification";

import { useAcceptAppointment } from "@hooks/mutations/appointment/useAcceptAppointment";
import { useCancelAppointment } from "@hooks/mutations/appointment/useCancelAppointment";
import { useUpdateAppointment } from "@hooks/mutations/appointment/useUpdateAppointment";
import { useCreateConsultationWithAppointment } from "@hooks/mutations/consultation/useCreateConsultationWithAppointment";
import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import { Appointment, AppointmentCategory } from "@customTypes/appointment";

import { AnalysisPrescriptionDocumentsModal } from "../AnalysisPrescriptionDocumentsModal";
import { AnalysisTestsModal } from "../AnalysisTestsModal";
import { EditLabAppointmentModal } from "../EditLabAppointmentModal";
import { StyledTableControls } from "../TableControls/TableControls.styled";


interface LabAppointmentTableControlsProps {
  row: Appointment;
  doctors: ComboboxItem[];
}

export const LabAppointmentTableControls = ({
  row,
  doctors,
}: LabAppointmentTableControlsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [
    editLabApptModalOpened,
    { open: openEditLabApptModal, close: closeEditLabApptModal },
  ] = useDisclosure(false);

  const [
    analysisDocumentsModalOpened,
    { open: openAnalysisDocumentsModal, close: closeAnalysisDocumentsModal },
  ] = useDisclosure(false);
  const [
    analysisTestsModalOpened,
    { open: openAnalysisTestsModal, close: closeAnalysisTestsModal },
  ] = useDisclosure(false);

  const { mutate: updateAppointment, isLoading: isUpdateAppointmentLoading } =
    useUpdateAppointment();
  const {
    mutate: createConsultationWithAppointment,
    isLoading: isCreateConsultationWithAppointmentLoading,
  } = useCreateConsultationWithAppointment();
  const { mutate: cancelAppointment, isLoading: isCancelAppointmentLoading } =
    useCancelAppointment();

  const handleErrors = useQueryErrorHandling();

  return (
    <>
      <StyledTableControls className="table-controls">
        <EditLabAppointmentModal
          opened={editLabApptModalOpened}
          close={closeEditLabApptModal}
          selectedRow={row}
          doctors={doctors}
          variant={"booked"}
        />
        <AnalysisPrescriptionDocumentsModal
          files={(row.files as string[]) ?? []}
          opened={analysisDocumentsModalOpened}
          close={closeAnalysisDocumentsModal}
        />
        <AnalysisTestsModal
          selectedRow={row}
          opened={analysisTestsModalOpened}
          close={closeAnalysisTestsModal}
        />

        <Menu withArrow position="bottom-end" withinPortal>
          <Menu.Target>
            <Button
              variant="outline"
              size="compact-sm"
              loading={
                isUpdateAppointmentLoading || isUpdateAppointmentLoading
              }>
              {t("action")}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconEye size={16} stroke={1.5} />}
              onClick={openAnalysisTestsModal}>
              {t("view-analysis-tests")}
            </Menu.Item>

            <Menu.Item
              leftSection={<IconEye size={16} stroke={1.5} />}
              onClick={openAnalysisDocumentsModal}>
              {t("view-analysis-documents")}
            </Menu.Item>

            <PermissionGate permission="appointments.edit"><Menu.Item
              leftSection={<IconPencil size={16} stroke={1.5} />}
              onClick={openEditLabApptModal}>
              {t("edit-appointment")}
            </Menu.Item></PermissionGate>

            <Menu.Divider />

            <PermissionGate permission="appointments.cancel"><Menu.Item
              leftSection={<IconX size={16} stroke={1.5} />}
              color="red"
              onClick={() => {
                openDeleteModal({
                  text: t("cancel-selected-appointment"),
                  confirmText: t("cancel-appointment"),
                  onConfirm: () => {
                    const id = showLoadingNotification({});
                    cancelAppointment(
                      {
                        appointmentId: row?.id,
                      },
                      {
                        onSuccess: () => {
                          showSuccessNotification({
                            id,
                            title: "success",
                            message: "cancel-appointment-success",
                          });
                        },
                        onError: (res: any) => {
                          handleErrors(res, "mutation", id);
                        },
                      }
                    );
                  },
                });
              }}>
              {t("cancel-appointment")}
            </Menu.Item></PermissionGate>
          </Menu.Dropdown>
        </Menu>
      </StyledTableControls>
      <LastUpdatedTimestamp
        updatedDate={row?.updatedAt as Date}
        className="last-updated"
      />
    </>
  );
};
